<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :width="size"
    :height="size"
    viewBox="0 0 48 48"
    fill="none"
    :class="['priority-icon', `priority-icon-${priorityLevel}`]"
  >
    <!-- Circular background with light color - large enough to contain all chevrons -->
    <circle
      cx="24"
      cy="24"
      r="22"
      :fill="getBackgroundColor()"
      :stroke="getBorderColor()"
      stroke-width="2"
    />
    <!-- Chevrons for priority levels - centered at (24, 24) -->
    <!-- Highest: 3 up chevrons -->
    <g v-if="priorityLevel === 4">
      <path
        d="M16 22 L24 14 L32 22"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
      <path
        d="M16 28 L24 20 L32 28"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
      <path
        d="M16 34 L24 26 L32 34"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </g>
    <!-- High: 2 up chevrons -->
    <g v-else-if="priorityLevel === 3">
      <path
        d="M18 24 L24 18 L30 24"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
      <path
        d="M18 30 L24 24 L30 30"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </g>
    <!-- Medium: 1 up chevron -->
    <g v-else-if="priorityLevel === 2">
      <path
        d="M20 26 L24 22 L28 26"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </g>
    <!-- Low: 1 down chevron -->
    <g v-else-if="priorityLevel === 1">
      <path
        d="M20 22 L24 26 L28 22"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </g>
    <!-- Lowest: 2 down chevrons -->
    <g v-else>
      <path
        d="M18 20 L24 24 L30 20"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
      <path
        d="M18 26 L24 30 L30 26"
        :stroke="getBarColor(1)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </g>
  </svg>
</template>

<script>
export default {
  name: 'PriorityIcon',
  props: {
    priority: {
      type: [String, Number],
      required: true,
      validator: value => {
        const validValues = ['lowest', 'low', 'medium', 'high', 'highest', 0, 1, 2, 3, 4]
        const normalized = typeof value === 'string' ? value.toLowerCase() : value
        return validValues.includes(normalized)
      },
    },
    size: {
      type: [Number, String],
      default: 16,
    },
    color: {
      type: String,
      default: null, // null means use default colors based on priority
    },
  },
  computed: {
    priorityLevel() {
      // Convert priority to numeric level (0-4)
      if (typeof this.priority === 'number') {
        return this.priority
      }
      const priorityMap = {
        lowest: 0,
        low: 1,
        medium: 2,
        high: 3,
        highest: 4,
      }
      return priorityMap[this.priority.toLowerCase()] ?? 2 // Default to medium
    },
  },
  methods: {
    getBarColor(barNumber) {
      // If custom color is provided, use it for all bars
      if (this.color) {
        return this.color
      }

      // Default colors based on priority level
      const colors = {
        0: '#9CA3AF', // lowest - gray
        1: '#60A5FA', // low - blue
        2: '#FBBF24', // medium - yellow/amber
        3: '#F97316', // high - orange
        4: '#EF4444', // highest - red
      }

      // Return color based on the current priority level
      return colors[this.priorityLevel] || colors[2]
    },
    getBackgroundColor() {
      // Light/tinted background colors for each priority level
      const backgroundColors = {
        0: '#F3F4F6', // lowest - very light gray
        1: '#DBEAFE', // low - light blue
        2: '#FEF3C7', // medium - light amber/yellow
        3: '#FFEDD5', // high - light orange
        4: '#FEE2E2', // highest - light red
      }

      return backgroundColors[this.priorityLevel] || backgroundColors[2]
    },
    getBorderColor() {
      // Accented border colors - darker versions of priority colors
      const borderColors = {
        0: '#9CA3AF', // lowest - gray
        1: '#3B82F6', // low - darker blue
        2: '#F59E0B', // medium - darker amber
        3: '#EA580C', // high - darker orange
        4: '#DC2626', // highest - darker red
      }

      return borderColors[this.priorityLevel] || borderColors[2]
    },
  },
}
</script>

<style scoped>
.priority-icon {
  display: inline-block;
  vertical-align: middle;
}

.priority-icon-lowest {
  color: #9ca3af; /* gray */
}

.priority-icon-low {
  color: #60a5fa; /* blue */
}

.priority-icon-medium {
  color: #fbbf24; /* amber/yellow */
}

.priority-icon-high {
  color: #f97316; /* orange */
}

.priority-icon-highest {
  color: #ef4444; /* red */
}
</style>
